/*
 * File: Home.jsx
 * Project: our-wave-stories-app
 *
 * Created by Brendan Michaelsen on June 13, 2024 at 9:21 AM
 * Copyright © 2024 Our Wave, Inc. All rights reserved.
 *
 * Last Modified: June 1, 2025 at 5:21 PM
 * Modified By: Brendan Michaelsen
 */

/**
 * Imports
 */

// Modules
import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { Trans, useTranslation } from 'react-i18next';

// Utilities
import { buildDynamicMeta } from '../../../components/Meta';
import { formatTagFamilies } from '../../../utilities/tags';

// Store
import { fetchTags } from '../../../store/slices/storyTag/storyTag.slice';

// Services
import { recordView } from '../../../services/view';

// Components
import {
	Meta,
	AppNavigation,
	Typography,
	SchemaScript,
	CommunityPanel,
} from '../../../components';

// Tabs
import { FeedComponent } from './FeedComponent';

// Constants
import { AVAILABLE_I18N_NAMESPACES, SOURCES } from '../../../../constants/base.constants';
import { PANEL_VARIANTS } from '../../../components/CommunityPanel/CommunityPanel';

// Styles
import * as S from './Home.styles';


/**
 * Constants
 */

const CONTENT_FILTER = {
	ALL: 'all',
	MESSAGES: 'messages',
	STORIES: 'stories'
};


/**
 * Component
 */

const Home = ({ meta: m }) => {

	// Create state
	const [contentFilter, setContentFilter] = useState(CONTENT_FILTER.ALL);

	// Get current locale from hook
	const locale = useSelector((state) => state.locale.value);

	// Get current partner from hook
	const partner = useSelector((state) => state.partner.value);

	// Get current story tags from hook
	const { status: tagStatus, value: storyTags } = useSelector((state) => state.storyTag);

	// Get translation hook
	const [t] = useTranslation(AVAILABLE_I18N_NAMESPACES.CLIENT);

	// Use hooks
	const dispatch = useDispatch();

	// Define meta content
	const meta = useMemo(() => ({
		title: t('pageHome.metaTitle', { partnerName: partner.name }),
		description: t('pageHome.metaDescription', { partnerName: partner.name }),
		bodyClasses: 'hide-captcha',
		...m || {}
	}), [m, partner, t]);

	// Create partner domain
	const partnerDomain = useMemo(() => {
		let domain = `https://${partner.domain}`;
		if (partner.redirect_to_subdomain) {
			domain = `https://${partner.redirect_to_subdomain}.${partner.domain}`;
		}
		return domain;
	}, [partner]);

	// Organize tags
	const tagFamilies = useMemo(() => formatTagFamilies(storyTags, t, partner?.id, true), [storyTags, partner, t]);

	// Handle actions on component load
	useEffect(() => {

		// Fetch story tags if necessary
		if (tagStatus !== 'final') {

			// Fetch story tags
			dispatch(fetchTags());
		}

		// Record view
		recordView({
			source: SOURCES.STORIES,
			urlPath: window.location.pathname
		});

	}, []);

	// Render component
	return (
		<>
			{/* Meta */}
			<Meta meta={meta} data={{}} />

			{/* Schema.org */}
			<SchemaScript schema={{
				'@context': 'http://schema.org',
				'@type': 'BreadcrumbList',
				'@id': `${partnerDomain}/feed/#BreadcrumbList`,
				itemListElement: [
					{
						'@type': 'ListItem',
						position: 1,
						item: {
							'@id': `${partnerDomain}/feed/#ListItem`,
							name: buildDynamicMeta(meta.title, { partner }),
							url: `${partnerDomain}${locale.localePath}`
						}
					}
				],
			}}
			/>

			{/* Component Content */}
			<AppNavigation showFooter>
				<S.Wrapper>

					{/* Header */}
					<S.Header>

						{/* Title */}
						<Typography tag="h1" weight="black">
							<Trans t={t} i18nKey="pageHome.title">
								Community
							</Trans>
						</Typography>

						{/* Content Switcher */}
						<S.ContentSwitcher className="animate">

							{/* All */}
							<S.SwitcherTab
								onClick={() => { setContentFilter(CONTENT_FILTER.ALL); }}
								$isActive={contentFilter === CONTENT_FILTER.ALL}
								className="animate"
							>
								<Typography weight="medium">
									<Trans t={t} i18nKey="pageHome.contentFilterAll">
										All
									</Trans>
								</Typography>
							</S.SwitcherTab>

							{/* Messages */}
							<S.SwitcherTab
								onClick={() => { setContentFilter(CONTENT_FILTER.MESSAGES); }}
								$isActive={contentFilter === CONTENT_FILTER.MESSAGES}
								className="animate"
							>
								<Typography weight="medium">
									<Trans t={t} i18nKey="pageHome.contentFilterMessages">
										Community Messages
									</Trans>
								</Typography>
							</S.SwitcherTab>

							{/* Stories */}
							<S.SwitcherTab
								onClick={() => { setContentFilter(CONTENT_FILTER.STORIES); }}
								$isActive={contentFilter === CONTENT_FILTER.STORIES}
								className="animate"
							>
								<Typography weight="medium">
									<Trans t={t} i18nKey="pageHome.contentFilterStories">
										Survivor Stories
									</Trans>
								</Typography>
							</S.SwitcherTab>

						</S.ContentSwitcher>
					</S.Header>

					{/* Tab Container */}
					<S.PanelContainer>

						{/* Feed */}
						<S.ComponentPanel>
							<FeedComponent meta={meta} tagFamilies={tagFamilies} activeContentFilter={contentFilter} />
						</S.ComponentPanel>

						{/* Community Panel */}
						<CommunityPanel variant={PANEL_VARIANTS.HOME} offset={40} className="communityPanel" />

					</S.PanelContainer>

				</S.Wrapper>
			</AppNavigation>
		</>
	);
};


/**
 * Configuration
 */

Home.propTypes = {
	meta: PropTypes.shape()
};
Home.defaultProps = {
	meta: {}
};


/**
 * Exports
 */

export default Home;
